import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFinancialYear } from '../context/FinancialYearContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  FileText,
  Plus,
  Pencil,
  Trash2,
  Calendar,
  PieChart,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUARTERS = [
  { value: 'Q1', label: 'Q1 (Apr-Jun)' },
  { value: 'Q2', label: 'Q2 (Jul-Sep)' },
  { value: 'Q3', label: 'Q3 (Oct-Dec)' },
  { value: 'Q4', label: 'Q4 (Jan-Mar)' }
];

const PnLPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const { selectedFY: globalFY } = useFinancialYear();
  const [activeTab, setActiveTab] = useState('quarterly');
  
  // State - use global FY
  const [reports, setReports] = useState([]);
  const [ytdData, setYtdData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form modal state
  const [formOpen, setFormOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [formData, setFormData] = useState({
    financial_year: '',
    quarter: '',
    revenue: '',
    training_expenses: '',
    hr_salary_expenses: '',
    hr_other_expenses: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Auto-populate state
  const [fetchingExpenses, setFetchingExpenses] = useState(false);

  // Funder-method P&L
  const [funderPnl, setFunderPnl] = useState(null);
  const [funderPnlLoading, setFunderPnlLoading] = useState(false);
  const [statusScope, setStatusScope] = useState('commitment'); // 'commitment' or 'realized'

  const canManage = user?.role === 'ADMIN' || user?.role === 'ACCOUNTANT';

  // Fetch reports for selected FY
  const fetchReports = useCallback(async () => {
    if (!globalFY) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API}/pnl/reports?financial_year=${globalFY}`, {
        headers: getAuthHeaders()
      });
      setReports(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch P&L reports:', error);
      toast.error('Failed to load P&L reports');
    } finally {
      setLoading(false);
    }
  }, [globalFY, getAuthHeaders]);

  // Fetch YTD data
  const fetchYTD = useCallback(async () => {
    if (!globalFY) return;
    try {
      const response = await axios.get(`${API}/pnl/ytd/${globalFY}`, {
        headers: getAuthHeaders()
      });
      setYtdData(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch YTD data:', error);
    }
  }, [globalFY, getAuthHeaders]);

  // Fetch Funder-method P&L
  const fetchFunderPnl = useCallback(async () => {
    if (!globalFY) return;
    try {
      setFunderPnlLoading(true);
      const response = await axios.get(`${API}/pnl/funder-quarterly?financial_year=${globalFY}&status_scope=${statusScope}`, {
        headers: getAuthHeaders()
      });
      setFunderPnl(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch funder P&L:', error);
      toast.error('Failed to load Funder P&L');
    } finally {
      setFunderPnlLoading(false);
    }
  }, [globalFY, statusScope, getAuthHeaders]);

  useEffect(() => {
    if (globalFY && activeTab === 'funder') fetchFunderPnl();
  }, [globalFY, activeTab, statusScope, fetchFunderPnl]);

  useEffect(() => {
    if (globalFY) {
      fetchReports();
      fetchYTD();
    }
  }, [globalFY, fetchReports, fetchYTD]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Open form for new entry
  const openNewForm = () => {
    setEditingReport(null);
    setFormData({
      financial_year: globalFY,
      quarter: '',
      revenue: '',
      training_expenses: '',
      hr_salary_expenses: '',
      hr_other_expenses: '',
      notes: ''
    });
    setFormOpen(true);
  };

  // Open form for editing
  const openEditForm = (report) => {
    setEditingReport(report);
    setFormData({
      financial_year: report.financial_year,
      quarter: report.quarter,
      revenue: report.revenue.toString(),
      training_expenses: report.training_expenses.toString(),
      hr_salary_expenses: report.hr_salary_expenses.toString(),
      hr_other_expenses: report.hr_other_expenses.toString(),
      notes: report.notes || ''
    });
    setFormOpen(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.quarter) {
      toast.error('Please select a quarter');
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API}/pnl/reports`, {
        financial_year: formData.financial_year,
        quarter: formData.quarter,
        revenue: parseFloat(formData.revenue) || 0,
        training_expenses: parseFloat(formData.training_expenses) || 0,
        hr_salary_expenses: parseFloat(formData.hr_salary_expenses) || 0,
        hr_other_expenses: parseFloat(formData.hr_other_expenses) || 0,
        notes: formData.notes
      }, { headers: getAuthHeaders() });
      
      toast.success(editingReport ? 'P&L report updated' : 'P&L report created');
      setFormOpen(false);
      fetchReports();
      fetchYTD();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save P&L report');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!reportToDelete) return;
    try {
      setDeleting(true);
      await axios.delete(
        `${API}/pnl/reports/${reportToDelete.financial_year}/${reportToDelete.quarter}`,
        { headers: getAuthHeaders() }
      );
      toast.success('P&L report deleted');
      setDeleteDialogOpen(false);
      setReportToDelete(null);
      fetchReports();
      fetchYTD();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete P&L report');
    } finally {
      setDeleting(false);
    }
  };

  // Auto-populate expenses from Summary Report
  const fetchExpensesFromSummary = async () => {
    if (!formData.financial_year || !formData.quarter) {
      toast.error('Please select Financial Year and Quarter first');
      return;
    }
    
    try {
      setFetchingExpenses(true);
      const response = await axios.get(
        `${API}/historical/summary/${formData.financial_year}`,
        { headers: getAuthHeaders() }
      );
      
      // Find the quarter data
      const quarterData = response.data.quarterly?.find(q => q.quarter === formData.quarter);
      
      if (quarterData) {
        setFormData(prev => ({
          ...prev,
          training_expenses: quarterData.training?.total?.toString() || '0',
          hr_salary_expenses: quarterData.hr_salary?.total?.toString() || '0',
          hr_other_expenses: quarterData.hr_other?.total?.toString() || '0'
        }));
        toast.success('Expenses auto-populated from Summary Report');
      } else {
        toast.info('No expense data found for this quarter');
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch expenses:', error);
      toast.error('Failed to fetch expenses from Summary Report');
    } finally {
      setFetchingExpenses(false);
    }
  };

  // Get quarter data
  const getQuarterReport = (quarter) => {
    return reports.find(r => r.quarter === quarter);
  };

  // Render P&L statement
  const renderPnLStatement = (data, title) => {
    if (!data) return null;
    
    // Calculate values using new formula
    const grossProfit = data.revenue - data.training_expenses;
    const grossProfitPercent = data.revenue > 0 ? ((grossProfit / data.revenue) * 100).toFixed(1) : 0;
    const totalHrExpenses = data.hr_salary_expenses + data.hr_other_expenses;
    const netProfit = grossProfit - totalHrExpenses;
    const partnerShare = netProfit > 0 ? netProfit * 0.5 : 0;
    const yourShare = netProfit - partnerShare;
    
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {/* Revenue */}
          <div className="flex justify-between items-center px-4 py-3 bg-emerald-50 border-b border-slate-200">
            <span className="font-medium text-slate-700">Revenue</span>
            <span className="font-semibold text-emerald-700">{formatCurrency(data.revenue)}</span>
          </div>
          
          {/* Training Expenses */}
          <div className="border-b border-slate-200">
            <div className="flex justify-between items-center px-4 py-2 pl-8 bg-slate-50">
              <span className="text-slate-600">Less: Training Expenses</span>
              <span className="text-red-600">({formatCurrency(data.training_expenses)})</span>
            </div>
          </div>
          
          {/* Gross Profit */}
          <div className={`flex justify-between items-center px-4 py-3 border-b border-slate-200 ${grossProfit >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-700">Gross Profit</span>
              {data.revenue > 0 && (
                <span className={`text-sm px-2 py-0.5 rounded-full ${grossProfit >= 0 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                  {grossProfitPercent}%
                </span>
              )}
            </div>
            <span className={`font-semibold ${grossProfit >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
              {formatCurrency(grossProfit)}
            </span>
          </div>
          
          {/* HR Expenses Section */}
          <div className="border-b border-slate-200">
            <div className="px-4 py-2 bg-slate-50 text-sm font-medium text-slate-500">
              Less: HR Expenses
            </div>
            <div className="flex justify-between items-center px-4 py-2 pl-8 border-b border-slate-100">
              <span className="text-slate-600">HR (Salary) Expenses</span>
              <span className="text-red-600">({formatCurrency(data.hr_salary_expenses)})</span>
            </div>
            <div className="flex justify-between items-center px-4 py-2 pl-8 border-b border-slate-100">
              <span className="text-slate-600">HR (Other) Expenses</span>
              <span className="text-red-600">({formatCurrency(data.hr_other_expenses)})</span>
            </div>
            <div className="flex justify-between items-center px-4 py-2 bg-red-50 font-medium">
              <span className="text-slate-700">Total HR Expenses</span>
              <span className="text-red-700">({formatCurrency(totalHrExpenses)})</span>
            </div>
          </div>
          
          {/* Net Profit */}
          <div className={`flex justify-between items-center px-4 py-3 border-b border-slate-200 ${netProfit >= 0 ? 'bg-purple-50' : 'bg-red-50'}`}>
            <span className="font-medium text-slate-700">Net Profit</span>
            <span className={`font-semibold ${netProfit >= 0 ? 'text-purple-700' : 'text-red-700'}`}>
              {formatCurrency(netProfit)}
            </span>
          </div>
          
          {/* Partner Share (50-50 split between N & R) */}
          {netProfit > 0 && (
            <div className={`flex justify-between items-center px-4 py-4 bg-indigo-100`}>
              <span className="font-bold text-slate-900">Partner Share (N & R, 50% each)</span>
              <span className="font-bold text-lg text-indigo-700">
                {formatCurrency(partnerShare)} each
              </span>
            </div>
          )}
          {netProfit <= 0 && (
            <div className="flex justify-between items-center px-4 py-4 bg-red-100">
              <span className="font-bold text-slate-900">Partner Share (N & R)</span>
              <span className="font-bold text-lg text-red-700">No profit to share</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="pnl-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <PieChart className="w-7 h-7 text-indigo-600" />
            P&L Reporting - FY {globalFY}
          </h1>
          <p className="text-slate-500 mt-1">Quarterly profit and loss statements with N & R split</p>
        </div>
        <div className="flex items-center gap-3">
          {canManage && (
            <Button 
              onClick={openNewForm}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="add-pnl-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Quarter
            </Button>
          )}
        </div>
      </div>

      {/* YTD Summary Cards */}
      {ytdData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-600">YTD Revenue</p>
                  <p className="text-2xl font-bold text-emerald-700">{formatCurrency(ytdData.revenue)}</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">YTD Gross Profit</p>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(ytdData.gross_profit)}</p>
                    {ytdData.revenue > 0 && (
                      <span className="text-sm px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        {ytdData.gross_profit_percent || ((ytdData.revenue - ytdData.training_expenses) / ytdData.revenue * 100).toFixed(1)}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">YTD Net Profit</p>
                  <p className="text-2xl font-bold text-purple-700">{formatCurrency(ytdData.net_profit)}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-indigo-600">Partner Share (N & R, 50% each)</p>
                  <p className="text-2xl font-bold text-indigo-700">{formatCurrency(ytdData.partner_share || (ytdData.net_profit > 0 ? ytdData.net_profit * 0.5 : 0))} each</p>
                </div>
                <div className="p-3 bg-indigo-100 rounded-full">
                  <DollarSign className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="quarterly" className="data-[state=active]:bg-white">
            <Calendar className="w-4 h-4 mr-2" />
            Quarterly View
          </TabsTrigger>
          <TabsTrigger value="ytd" className="data-[state=active]:bg-white">
            <BarChart3 className="w-4 h-4 mr-2" />
            YTD Summary
          </TabsTrigger>
          <TabsTrigger value="funder" className="data-[state=active]:bg-white" data-testid="tab-funder-pnl">
            <PieChart className="w-4 h-4 mr-2" />
            Funder Method P&L
          </TabsTrigger>
        </TabsList>

        {/* Quarterly View */}
        <TabsContent value="quarterly" className="mt-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {QUARTERS.map(q => {
                const report = getQuarterReport(q.value);
                return (
                  <Card key={q.value} className="border-slate-200">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-indigo-600" />
                          {q.label}
                        </CardTitle>
                        {report && canManage && (
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditForm(report)}
                              data-testid={`edit-${q.value}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {user?.role === 'ADMIN' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => { setReportToDelete(report); setDeleteDialogOpen(true); }}
                                data-testid={`delete-${q.value}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {report ? (
                        renderPnLStatement(report, '')
                      ) : (
                        <div className="py-8 text-center">
                          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                          <p className="text-slate-500 text-sm">No data for this quarter</p>
                          {canManage && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-3"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, financial_year: globalFY, quarter: q.value }));
                                setEditingReport(null);
                                setFormOpen(true);
                              }}
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add Data
                            </Button>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* YTD Summary */}
        <TabsContent value="ytd" className="mt-6">
          {ytdData ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* YTD P&L Statement */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-indigo-600" />
                    Year-to-Date P&L Statement
                  </CardTitle>
                  <CardDescription>
                    FY {globalFY} • {ytdData.quarters_reported} quarter(s) reported
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {renderPnLStatement(ytdData, '')}
                </CardContent>
              </Card>

              {/* Quarterly Comparison */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-indigo-600" />
                    Quarterly Comparison
                  </CardTitle>
                  <CardDescription>
                    Quarter-wise breakdown for FY {globalFY}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {ytdData.quarterly_breakdown && ytdData.quarterly_breakdown.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-2 font-medium text-slate-500">Quarter</th>
                            <th className="text-right py-2 px-2 font-medium text-slate-500">Revenue</th>
                            <th className="text-right py-2 px-2 font-medium text-slate-500">Expenses</th>
                            <th className="text-right py-2 px-2 font-medium text-slate-500">Net Profit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ytdData.quarterly_breakdown.map(q => (
                            <tr key={q.quarter} className="border-b border-slate-100">
                              <td className="py-2 px-2 font-medium">{q.quarter}</td>
                              <td className="py-2 px-2 text-right text-emerald-600">{formatCurrency(q.revenue)}</td>
                              <td className="py-2 px-2 text-right text-red-600">{formatCurrency(q.total_expenses)}</td>
                              <td className={`py-2 px-2 text-right font-semibold ${q.net_profit >= 0 ? 'text-indigo-600' : 'text-red-600'}`}>
                                {formatCurrency(q.net_profit)}
                              </td>
                            </tr>
                          ))}
                          <tr className="bg-slate-50 font-semibold">
                            <td className="py-2 px-2">Total (YTD)</td>
                            <td className="py-2 px-2 text-right text-emerald-700">{formatCurrency(ytdData.revenue)}</td>
                            <td className="py-2 px-2 text-right text-red-700">{formatCurrency(ytdData.total_expenses)}</td>
                            <td className={`py-2 px-2 text-right ${ytdData.net_profit >= 0 ? 'text-indigo-700' : 'text-red-700'}`}>
                              {formatCurrency(ytdData.net_profit)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <BarChart3 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">No quarterly data available</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          )}
        </TabsContent>

        {/* Funder Method P&L */}
        <TabsContent value="funder" className="mt-6" data-testid="pane-funder-pnl">
          <Card className="border-slate-200 mb-4">
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-indigo-950">Funder Method P&L · FY {globalFY}</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Corp Team P&L = (Training Rev − Training Cost − 10% × Training Rev) + (Venue NP × 30%) − HR Salary − HR Other
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Label className="text-xs text-slate-600">HR expense scope</Label>
                <Select value={statusScope} onValueChange={setStatusScope}>
                  <SelectTrigger className="w-[220px]" data-testid="funder-pnl-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="commitment">All except Rejected (commitment)</SelectItem>
                    <SelectItem value="realized">Only Paid + Partially Paid (realized)</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={fetchFunderPnl} disabled={funderPnlLoading} data-testid="funder-pnl-refresh">
                  <RefreshCw className={`w-4 h-4 mr-2 ${funderPnlLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {funderPnlLoading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          )}
          {!funderPnlLoading && funderPnl && (
            <Card className="border-slate-200">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm" data-testid="funder-pnl-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600 min-w-[240px]">Line</th>
                      {['Q1', 'Q2', 'Q3', 'Q4'].map(q => (
                        <th key={q} className="text-right py-3 px-4 font-semibold text-slate-600">
                          {q}
                          <br />
                          <span className="text-[10px] font-normal text-slate-400">
                            {funderPnl.quarters[q].period.start.slice(5)}—{funderPnl.quarters[q].period.end.slice(5)}
                          </span>
                        </th>
                      ))}
                      <th className="text-right py-3 px-4 font-semibold text-indigo-950 bg-indigo-50">FY Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Training — Revenue (excl GST)', path: ['training', 'revenue_excl_gst'], tone: 'emerald' },
                      { label: 'Training — Project Cost', path: ['training', 'cost'], tone: 'rose' },
                      { label: 'Training — Profit (Rev − Cost)', path: ['training', 'profit'], bold: true },
                      { label: 'Training — Funder fee (10% × Rev)', path: ['training', 'funder_fee'], tone: 'amber' },
                      { label: 'Training — Corp Team Take', path: ['training', 'corp_team_take'], bold: true, tone: 'indigo' },
                      { divider: true },
                      { label: 'Venue — Revenue (excl GST)', path: ['venue', 'revenue_excl_gst'], tone: 'emerald' },
                      { label: 'Venue — Project Cost', path: ['venue', 'cost'], tone: 'rose' },
                      { label: 'Venue — Net Profit (W/O GST)', path: ['venue', 'net_profit_wo_gst'], bold: true },
                      { label: 'Venue — Funder fee (70% × NP)', path: ['venue', 'funder_fee'], tone: 'amber' },
                      { label: 'Venue — Corp Team Take (30%)', path: ['venue', 'corp_team_take'], bold: true, tone: 'indigo' },
                      { divider: true },
                      { label: 'Gross Corp Team Take (Training + Venue)', path: ['gross_corp_team_take'], bold: true, highlight: 'indigo' },
                      { label: 'HR Salary', path: ['hr_salary'], tone: 'rose', neg: true },
                      { label: 'HR Other', path: ['hr_other'], tone: 'rose', neg: true },
                      { label: 'HR Total', path: ['hr_total'], tone: 'rose', bold: true, neg: true },
                      { divider: true },
                      { label: 'NET P&L (Corp Team bottom line)', path: ['net_pnl'], bold: true, highlight: 'emerald', big: true }
                    ].map((row, idx) => {
                      if (row.divider) {
                        return <tr key={`div-${idx}`}><td colSpan={6} className="border-t border-slate-200 py-1"></td></tr>;
                      }
                      const getVal = (obj) => row.path.reduce((acc, k) => acc?.[k], obj);
                      const cls = (v) => {
                        const base = 'py-2 px-4 text-right font-mono';
                        const b = row.bold ? ' font-bold' : ' font-medium';
                        const big = row.big ? ' text-base' : '';
                        let color = ' text-slate-800';
                        if (row.highlight === 'emerald') color = ' text-emerald-700';
                        else if (row.highlight === 'indigo') color = ' text-indigo-700';
                        else if (row.tone === 'emerald' && v > 0) color = ' text-emerald-700';
                        else if (row.tone === 'rose' && v > 0) color = ' text-rose-700';
                        else if (row.tone === 'amber' && v > 0) color = ' text-amber-700';
                        else if (row.tone === 'indigo' && v > 0) color = ' text-indigo-700';
                        return base + b + big + color;
                      };
                      const fmtRow = (v) => {
                        if (row.neg && v > 0) return '−' + formatCurrency(v);
                        return formatCurrency(v);
                      };
                      const rowBg = row.highlight === 'emerald' ? 'bg-emerald-50/50' : row.highlight === 'indigo' ? 'bg-indigo-50/50' : '';
                      return (
                        <tr key={row.label} className={`border-t border-slate-100 ${rowBg}`}>
                          <td className={`py-2 px-4 ${row.bold ? 'font-semibold' : ''} ${row.big ? 'text-base' : ''} text-slate-700`}>{row.label}</td>
                          {['Q1', 'Q2', 'Q3', 'Q4'].map(q => {
                            const v = getVal(funderPnl.quarters[q]) || 0;
                            return <td key={q} className={cls(v)}>{fmtRow(v)}</td>;
                          })}
                          <td className={cls(getVal(funderPnl.fy_total) || 0) + ' bg-indigo-50/70'}>
                            {fmtRow(getVal(funderPnl.fy_total) || 0)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="border-t border-slate-200 p-3 bg-slate-50 text-xs text-slate-600 flex flex-wrap gap-3 items-center">
                  <span><strong>Training projects (FY):</strong> {funderPnl.fy_total.training.project_count}</span>
                  <span>•</span>
                  <span><strong>Venue projects (FY):</strong> {funderPnl.fy_total.venue.project_count}</span>
                  <span>•</span>
                  <span><strong>HR entries (FY):</strong> {funderPnl.fy_total.hr_count}</span>
                  <span>•</span>
                  <span><strong>Scope:</strong> {statusScope === 'realized' ? 'Realized (Paid + Partially Paid)' : 'Commitment (all except Rejected)'}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Form Modal */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="pnl-form-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              {editingReport ? 'Edit P&L Report' : 'Add P&L Report'}
            </DialogTitle>
            <DialogDescription>
              Enter the quarterly financial data for FY {globalFY}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Financial Year</Label>
                <div className="mt-1.5 h-10 px-3 py-2 bg-slate-100 rounded-md border text-sm text-slate-700 flex items-center">
                  FY {formData.financial_year}
                </div>
              </div>
              <div>
                <Label>Quarter</Label>
                <Select 
                  value={formData.quarter} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, quarter: v }))}
                  disabled={!!editingReport}
                >
                  <SelectTrigger className="mt-1.5" data-testid="quarter-select">
                    <SelectValue placeholder="Select Quarter" />
                  </SelectTrigger>
                  <SelectContent>
                    {QUARTERS.map(q => (
                      <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-200">
              <Label className="text-emerald-700 font-medium">Revenue</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                <Input
                  type="number"
                  value={formData.revenue}
                  onChange={(e) => setFormData(prev => ({ ...prev, revenue: e.target.value }))}
                  placeholder="0"
                  className="pl-7"
                  data-testid="revenue-input"
                />
              </div>
            </div>

            <div className="pt-2 border-t border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-red-700">Expenses</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={fetchExpensesFromSummary}
                  disabled={fetchingExpenses || !formData.quarter}
                  className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                  data-testid="auto-populate-btn"
                >
                  {fetchingExpenses ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-indigo-600 mr-2"></div>
                  ) : (
                    <RefreshCw className="w-3 h-3 mr-2" />
                  )}
                  Auto-Populate from Summary
                </Button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-600">Training Expenses</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <Input
                      type="number"
                      value={formData.training_expenses}
                      onChange={(e) => setFormData(prev => ({ ...prev, training_expenses: e.target.value }))}
                      placeholder="0"
                      className="pl-7"
                      data-testid="training-expenses-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-600">HR (Salary) Expenses</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <Input
                      type="number"
                      value={formData.hr_salary_expenses}
                      onChange={(e) => setFormData(prev => ({ ...prev, hr_salary_expenses: e.target.value }))}
                      placeholder="0"
                      className="pl-7"
                      data-testid="hr-salary-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-600">HR (Other) Expenses</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <Input
                      type="number"
                      value={formData.hr_other_expenses}
                      onChange={(e) => setFormData(prev => ({ ...prev, hr_other_expenses: e.target.value }))}
                      placeholder="0"
                      className="pl-7"
                      data-testid="hr-other-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                rows={2}
                className="mt-1.5"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-pnl-btn"
            >
              {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
              {editingReport ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete P&L Report</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the P&L report for{' '}
              <strong>{reportToDelete?.financial_year} {reportToDelete?.quarter}</strong>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PnLPage;
